import React, { useState } from 'react';
import { toolsAPI } from '../../lib/api';

const TOOL_KEY = 'standings-updater';

const FIELD_CLASS = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';

function PresetEditor({ preset, onCancel, onSaved }) {
  const cfg = preset.config || {};
  const initial = (cfg.sources || []).length ? cfg.sources : [{ championship_url: '', sheet_url: '' }];
  const [name, setName] = useState(preset.name);
  const [sources, setSources] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function updateSource(i, field, value) {
    setSources(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  }
  function addSource() { setSources(prev => [...prev, { championship_url: '', sheet_url: '' }]); }
  function removeSource(i) { setSources(prev => prev.filter((_, idx) => idx !== i)); }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const updated = await toolsAPI.updatePreset(TOOL_KEY, preset.id, {
        name: name.trim(),
        sources: sources.map(s => ({
          championship_url: s.championship_url.trim(),
          sheet_url: s.sheet_url.trim(),
        })),
      });
      onSaved(updated.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3 px-5 py-4 bg-[#0a0a0a]">
      {error && (
        <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Preset name</label>
        <input value={name} onChange={e => setName(e.target.value)} className={FIELD_CLASS} />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Pairs</label>
        <div className="space-y-2">
          {sources.map((src, i) => (
            <div key={i} className="bg-[#111111] border border-[#2a2a2a] rounded p-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Pair {i + 1}</span>
                <button
                  type="button"
                  onClick={() => removeSource(i)}
                  disabled={sources.length === 1}
                  className="text-gray-600 hover:text-red-400 disabled:opacity-30"
                  title="Remove"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <input
                placeholder="Speedhive championship URL"
                value={src.championship_url}
                onChange={e => updateSource(i, 'championship_url', e.target.value)}
                className={FIELD_CLASS}
              />
              <input
                placeholder="Google Sheet tab URL (with #gid=...)"
                value={src.sheet_url}
                onChange={e => updateSource(i, 'sheet_url', e.target.value)}
                className={FIELD_CLASS}
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addSource}
          className="mt-2 text-xs text-blue-500 hover:text-blue-400 transition-colors"
        >
          + Add another pair
        </button>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white">Cancel</button>
        <button
          onClick={handleSave}
          disabled={saving || !name.trim() || sources.some(s => !s.championship_url.trim() || !s.sheet_url.trim())}
          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default function ManagePresetsModal({ presets, onClose, onChanged }) {
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');

  async function handleDelete(preset) {
    if (!confirm(`Delete preset "${preset.name}"? This cannot be undone.`)) return;
    setDeletingId(preset.id);
    setError('');
    try {
      await toolsAPI.deletePreset(TOOL_KEY, preset.id);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a]">
          <h2 className="font-semibold text-white">Manage Presets</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {error && (
            <div className="m-5 px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>
          )}

          {presets.length === 0 ? (
            <div className="text-center py-12 text-gray-600 text-sm">No presets yet. Run the tool and save your first preset.</div>
          ) : (
            <div className="divide-y divide-[#2a2a2a]">
              {presets.map(p => (
                <div key={p.id}>
                  <div className="flex items-center gap-3 px-5 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium truncate">{p.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {p.config?.sources?.length || 0} pair{(p.config?.sources?.length || 0) !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => setEditingId(editingId === p.id ? null : p.id)}
                      className="text-xs text-blue-500 hover:text-blue-400"
                    >
                      {editingId === p.id ? 'Close' : 'Edit'}
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      disabled={deletingId === p.id}
                      className="text-xs text-red-500 hover:text-red-400 disabled:opacity-50"
                    >
                      {deletingId === p.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                  {editingId === p.id && (
                    <PresetEditor
                      preset={p}
                      onCancel={() => setEditingId(null)}
                      onSaved={() => { setEditingId(null); onChanged(); }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end px-5 py-4 border-t border-[#2a2a2a]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
